<template>
  <main class="callback-page">
    <template v-if="errorMessage">
      <h1 class="title is-4">Unable to complete sign in</h1>
      <p>{{ errorMessage }}</p>
      <button class="button is-primary" type="button" @click="retryAuthentication">
        Try signing in again
      </button>
    </template>
    <LoadingIndicator v-else message="Finishing sign in…" />
  </main>
</template>

<script setup lang="ts">

  import { onBeforeMount, ref } from "vue";

  import Auth from "@/auth";
  import LoadingIndicator from '@/components/LoadingIndicator.vue';
  import {useClientStore} from '@/stores/ClientStore';
  import {useCompanyStore} from "@/stores/CompanyStore";
  import {clientSubdomainMatches} from '@/use/clientSubdomain';

  const clientStore = useClientStore();
  const companyStore = useCompanyStore();
  const errorMessage = ref('');

  const retryAuthentication = () => {
    Auth.retryAuthentication();
  };

  onBeforeMount(async () => {
    try {
      const tokens = await Auth.getToken();
      Auth.setTokens(tokens);

      await companyStore.getCompany({
        params: {
          include: [
            'client.latestTerms',
            'primaryContact',
            'trades',
          ],
        }
      });

      const authenticatedClient = companyStore.company?.client;
      if (!authenticatedClient || !clientSubdomainMatches(authenticatedClient.subdomain)) {
        companyStore.clear();
        Auth.clearSession();
        errorMessage.value = 'Your sign-in session has expired or is no longer valid. Please try again.';
        return;
      }

      clientStore.setBranding(authenticatedClient);

      if (!companyStore.company?.latest_terms_accepted) {
        location.replace('/terms');
        return;
      }

      const redirectPath = window.sessionStorage.getItem('redirectPath') || '/';
      window.sessionStorage.removeItem('redirectPath');
      location.replace(redirectPath);
    } catch (err) {
      console.error(err);
      errorMessage.value = 'Your sign-in session has expired or is no longer valid. Please try again.';
    }
  });

</script>

<style lang="scss" scoped>
  .callback-page {
    align-items: center;
    display: flex;
    flex: 1;
    flex-direction: column;
    justify-content: center;
    margin: 0 auto;
    max-width: 30rem;
    text-align: center;

    .button {
      margin-top: 1.5rem;
    }
  }
</style>
